import React from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { PageShell } from "@/components/layout/PageShell";
import { Seo } from "@/components/seo/Seo";
import {
  BillingAddressFields,
  emptyBilling,
  type BillingDraft,
} from "@/components/give/BillingAddressFields";
import {
  RecognitionPicker,
  type RecognitionState,
} from "@/components/give/RecognitionPicker";
import { createWishOrder, type DonorPayload } from "@/services/give-api";
import { getStripe } from "@/services/stripe";
import { WISHLIST_ITEMS } from "@shared/wishlist";
import { STEWARD_ENTITY } from "@shared/campaign";

interface AmountPreset {
  label: string;
  value: number;
  hint?: string;
}

function presetsFor(item: { cost_usd: number; cadence: string; duration_months?: number }): AmountPreset[] {
  const full: AmountPreset = { label: "Full amount", value: item.cost_usd };
  const presets: AmountPreset[] = [full];
  if (item.cadence === "monthly" && item.duration_months) {
    const monthly = Math.round(item.cost_usd / item.duration_months);
    presets.push({
      label: "One month",
      value: monthly,
      hint: `${item.duration_months}-month commitment, paid as one`,
    });
  } else if (item.cost_usd >= 400) {
    presets.push({ label: "Half", value: Math.round(item.cost_usd / 2) });
  }
  return presets;
}

export function WishCheckout() {
  const { wishId } = useParams<{ wishId: string }>();
  const navigate = useNavigate();
  const item = WISHLIST_ITEMS.find((i) => i.id === wishId);

  const [billing, setBilling] = React.useState<BillingDraft>(() => emptyBilling("US"));
  const [amount, setAmount] = React.useState<number>(item?.cost_usd ?? 0);
  const [recognition, setRecognition] = React.useState<RecognitionState>({
    consent: "anonymous",
  });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  React.useEffect(() => {
    if (item) setAmount(item.cost_usd);
  }, [item?.id]);

  // Warm Stripe.js + the public-config fetch in the background while the
  // donor fills the form. By the time they land on /give/:orderId the SDK
  // is already loaded, so the card field renders without an extra wait.
  React.useEffect(() => {
    getStripe().catch(() => undefined);
  }, []);

  const presets = item ? presetsFor(item) : [];
  const isFullAmount = item ? amount === item.cost_usd : false;
  const fundingPct = item && item.cost_usd > 0
    ? Math.min(100, Math.round((amount / item.cost_usd) * 100))
    : 0;

  if (!item) {
    return (
      <PageShell title="Item not found" stewardship="Give · directed">
        <Seo title="Item not found" description="" path="/give" noindex />
        <p className="font-serif text-ink-2 mb-6 leading-relaxed">
          That wish-list item doesn&rsquo;t exist (any more, or yet).
        </p>
        <Link
          to="/foundation/contribute"
          className="font-mono text-xs uppercase tracking-widest text-ink underline decoration-hairline hover:decoration-ink"
        >
          ← Back to the wish list
        </Link>
      </PageShell>
    );
  }

  function billingFullName(): string {
    return `${billing.firstName} ${billing.lastName}`.trim();
  }

  async function onContinue(e: React.FormEvent) {
    e.preventDefault();
    if (submitting) return;
    if (!amount || amount < 1) {
      setError("Please enter an amount of US$1 or more.");
      return;
    }
    if (item && amount > item.cost_usd) {
      setError(`This item costs US$${item.cost_usd.toLocaleString("en-US")}. For a larger gift, please use general support.`);
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const donor: DonorPayload = {
        email: billing.email,
        firstName: billing.firstName,
        lastName: billing.lastName,
        phone: billing.phone || undefined,
        company: billing.company || undefined,
        address: {
          line1: billing.line1,
          line2: billing.line2 || undefined,
          city: billing.city,
          state: billing.state || undefined,
          postcode: billing.postcode,
          country: billing.country,
        },
      };
      const result = await createWishOrder({
        wishItemId: item!.id,
        donor,
        recognition,
        amountUsd: isFullAmount ? undefined : amount,
      });
      sessionStorage.setItem(
        `give-checkout-${result.orderId}`,
        JSON.stringify({
          donor,
          recognition,
          amountUsd: result.amountUsd,
          wishItemName: item!.name,
        }),
      );
      navigate(`/give/${result.orderId}`);
    } catch (err) {
      setError(String(err));
      setSubmitting(false);
    }
  }

  return (
    <PageShell
      title={item.name}
      subtitle={item.short}
      stewardship={`Give · directed · ${item.category}`}
    >
      <Seo
        title={`Support: ${item.name}`}
        description=""
        path={`/give/wish/${item.id}`}
        noindex
      />

      <Link
        to="/foundation/contribute#wishlist"
        className="font-mono text-[10px] uppercase tracking-widest text-ink-3 hover:text-ink mb-8 inline-block"
      >
        ← All wish-list items
      </Link>

      <div className="border border-hairline bg-paper p-6 space-y-3 text-sm text-ink-2 leading-relaxed mb-8">
        <p>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mr-2">
            Why this
          </span>
          {item.why_this}
        </p>
        <p>
          <span className="font-mono text-[10px] uppercase tracking-widest text-ink-3 mr-2">
            Unlocks
          </span>
          {item.what_it_unlocks}
        </p>
        <p className="font-mono text-xs text-ink">
          US${item.cost_usd.toLocaleString("en-US")} · Memo: DPF-WISH-{item.id.toUpperCase()}
        </p>
      </div>

      <div className="border border-hairline bg-paper-2 p-4 mb-8 text-xs font-mono text-ink-2 leading-relaxed">
        <strong className="text-ink">{STEWARD_ENTITY}</strong> is the temporary fiduciary steward for monetary support to the Foundation. Gifts during the stewardship period are <strong className="text-ink">not tax-deductible</strong>.
      </div>

      <form onSubmit={onContinue} className="space-y-8 max-w-2xl">
        <section>
          <h2 className="font-serif text-xl text-ink mb-4">Amount</h2>
          <div className="flex flex-wrap gap-2 mb-4">
            {presets.map((p) => (
              <button
                type="button"
                key={p.label}
                onClick={() => setAmount(p.value)}
                disabled={submitting}
                className={`px-4 py-2 font-mono text-xs uppercase tracking-widest border ${
                  amount === p.value
                    ? "bg-ink text-paper border-ink"
                    : "border-hairline text-ink hover:border-ink"
                }`}
                title={p.hint}
              >
                {p.label} · US${p.value.toLocaleString("en-US")}
              </button>
            ))}
          </div>
          <label className="block font-mono text-[10px] uppercase tracking-widest text-ink-3 mb-1" htmlFor="amount">
            Custom amount (USD)
          </label>
          <div className="flex items-baseline gap-3">
            <input
              id="amount"
              type="number"
              min={1}
              max={item.cost_usd}
              step={1}
              value={amount}
              onChange={(e) => setAmount(Number(e.target.value))}
              disabled={submitting}
              className="w-48 bg-paper border border-hairline px-3 py-2 font-mono text-sm text-ink focus:outline-none focus:border-ink"
            />
            <span className="font-mono text-[11px] text-ink-3">
              {fundingPct}% of US${item.cost_usd.toLocaleString("en-US")}
            </span>
          </div>
          <p className="mt-3 font-mono text-[11px] text-ink-3 leading-relaxed">
            Default is the full item cost. Adjust to make a partial gift — minimum US$1, maximum US${item.cost_usd.toLocaleString("en-US")}. Larger gifts go through general support.
          </p>
        </section>

        <section>
          <h2 className="font-serif text-xl text-ink mb-4">Billing details</h2>
          <BillingAddressFields value={billing} onChange={setBilling} disabled={submitting} />
        </section>

        <section>
          <RecognitionPicker
            billingFullName={billingFullName()}
            value={recognition}
            onChange={setRecognition}
            disabled={submitting}
          />
        </section>

        {error && (
          <p className="font-mono text-xs text-state-pending leading-relaxed">{error}</p>
        )}

        <button
          type="submit"
          disabled={submitting || !amount || amount < 1 || amount > item.cost_usd}
          className="w-full bg-ink text-paper px-6 py-4 font-mono text-xs uppercase tracking-widest hover:bg-ink-2 disabled:opacity-60"
        >
          {submitting
            ? "Preparing…"
            : `Continue — pay US$${amount.toLocaleString("en-US")}`}
        </button>
      </form>
    </PageShell>
  );
}
